<template>
    <section v-if="courseData" name="about" class="bg-lightest col-span-12 flex flex-col justify-between pt-4">
        <div class="grid grid-cols-12">
            <div class="gap-md col-span-12 flex flex-col px-2 lg:col-span-6 lg:col-start-4 lg:px-0">
                <TagComponent value="About course" class-name="font-bold" />

                <h1>{{ courseData.title }}</h1>
                <p class="whitespace-pre-line">{{ courseData.description }}</p>
                <ol class="px-sm py-sm mb-md rounded-md border border-(--border-subtle)">
                    <li
                        v-for="(item, index) in courseData.sections"
                        :key="item.title"
                        class="flex cursor-pointer flex-col"
                        :class="{
                            'mt-md': index !== 0,
                            'mb-md': index !== courseData.sections.length - 1,
                        }"
                        @click="toggleExpand(index)"
                    >
                        <div class="gap-xs lg:pl-sm flex items-center font-bold">
                            <i class="i-small-arrow-down" :class="{ '-rotate-90': !expandedIndices.includes(index) }" />
                            <h2>{{ index + 1 }} . {{ item.title }}</h2>
                        </div>
                        <ul v-show="expandedIndices.includes(index)">
                            <li
                                v-for="subItem in item.contents"
                                :key="subItem.title"
                                class="py-xs rounded-sm pl-[1.4rem] transition-colors hover:bg-(--surface-highlight) lg:pl-[2.4rem]"
                                :class="{ hidden: !subItem.title }"
                            >
                                <div class="gap-sm my-sm flex items-center">
                                    <span class="p-xs flex h-8 w-8 items-center justify-center rounded-sm bg-(--tag-primary)">
                                        <i
                                            :class="{
                                                'i-book': subItem.type.name !== 'video',
                                                'i-play': subItem.type.name === 'video',
                                            }"
                                        />
                                    </span>

                                    <p class="font-bold">
                                        {{ subItem.title.charAt(0).toUpperCase() + subItem.title.slice(1) }}
                                    </p>
                                </div>
                            </li>
                            <li
                                v-for="subItem in item.quizes"
                                :key="subItem.title"
                                class="py-xs rounded-sm pl-[2.4rem] transition-colors hover:bg-(--surface-highlight)"
                            >
                                <div class="gap-sm my-sm flex items-center">
                                    <span class="p-xs flex h-8 w-8 items-center justify-center rounded-sm bg-(--tag-primary)">
                                        <i class="i-book" />
                                    </span>

                                    <p class="font-bold">
                                        {{ subItem.title.charAt(0).toUpperCase() + subItem.title.slice(1) }}
                                    </p>
                                </div>
                            </li>
                        </ul>
                    </li>
                </ol>
            </div>
        </div>
        <div name="start" class="py-sm mt-auto grid grid-cols-12 border-t border-(--border-subtle)">
            <Button class="btn col-span-6 col-start-4 w-full" @:click="startCourse"> Start the course </Button>
        </div>
    </section>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { ref, onMounted, watch } from 'vue';
import lmsRouter from '@/router/lms-router';
import { useLMSStore } from '@/stores/lms';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import type { Course } from '@/stores/store-interfaces';

const lmsStore = useLMSStore();
const expandedIndices = ref<number[]>([]);
const courseData = ref<Course>();

onMounted(async () => {
    await setup();
});

const setup = async () => {
    if (lmsStore.pendingCourses[0] === undefined) return;

    courseData.value = await lmsStore.getCourse(lmsStore.pendingCourses[0].course_id);

    toggleExpand(0);
};

const toggleExpand = async (index: number) => {
    if (expandedIndices.value.includes(index)) {
        // Collapse the item
        expandedIndices.value = expandedIndices.value.filter((item) => item !== index);
    } else {
        expandedIndices.value.push(index);
    }
};

const startCourse = () => {
    lmsStore.setCourseStartTime();
    lmsRouter.push({ name: 'course', params: { courseId: lmsStore.pendingCourses[0].course_id } });
};

watch(
    () => lmsStore.pendingCourses,
    (newValue) => {
        if (newValue) setup();
    },
);
</script>
